import React, { useState } from 'react';
import { TextField as MuiTextField } from '@mui/material';

// Function to clean and filter text input (used only on blur)
const cleanText = (value) => {
    if (typeof value !== 'string') return value;

    return value
        .trim()                    // Remove leading and trailing whitespace only
        .replace(/\s+/g, ' ')      // Replace multiple spaces with single space
        .replace(/[-]{2,}/g, '-')  // Replace multiple dashes with single dash
        .replace(/['']{2,}/g, "'") // Replace multiple apostrophes with single
        .replace(/["]{2,}/g, '"'); // Replace multiple double quotes with single
};

// Function to check if character is allowed
const isAllowedChar = (char, allowNumbers = true, allowSpecialChars = [], allowSpaces = true) => {
    // Build base pattern - conditionally include spaces
    const spacePattern = allowSpaces ? '\\s' : '';
    const basePattern = new RegExp(`[\\p{L}\\p{M}${spacePattern}'\"\\-]`, 'u');

    if (basePattern.test(char)) return true;
    if (allowNumbers && /\d/.test(char)) return true;
    if (allowSpecialChars.includes(char)) return true;

    return false;
};

const TrimmedTextField = ({
    onChange,
    onBlur,
    allowNumbers = true,        // Option to allow/disallow numbers
    allowSpecialChars = [],     // Array of additional special chars to allow
    allowSpaces = true,         // Option to allow/disallow spaces
    showFeedback = true,        // Show feedback for invalid characters
    ...props
}) => {
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [showError, setShowError] = useState(false);

    const showInvalidCharFeedback = () => {
        if (!showFeedback) return;

        // Build list of allowed special characters
        let allowedSpecials = [`'`, `"`, `-`]; // Base allowed special chars
        if (allowSpecialChars.length > 0) {
            allowedSpecials = [...allowedSpecials, ...allowSpecialChars];
        }

        const specialCharsList = allowedSpecials.map(char => `"${char}"`).join(', ');
        setFeedbackMessage(`Only allowed special characters: ${specialCharsList}`);
        setShowError(true);

        // Clear feedback after 3 seconds (longer to read the message)
        setTimeout(() => {
            setFeedbackMessage('');
            setShowError(false);
        }, 3000);
    };

    // Handle key presses to prevent invalid characters
    const handleKeyPress = (event) => {
        const char = event.key;

        // Allow control keys (backspace, delete, arrow keys, etc.)
        if (char.length > 1) return;

        // Check if character is allowed
        if (!isAllowedChar(char, allowNumbers, allowSpecialChars, allowSpaces)) {
            event.preventDefault(); // Prevent the character from being entered
            showInvalidCharFeedback();
        }
    };

    // Handle paste events
    const handlePaste = (event) => {
        const pasteData = event.clipboardData.getData('text');
        const filteredData = pasteData
            .split('')
            .filter(char => isAllowedChar(char, allowNumbers, allowSpecialChars, allowSpaces))
            .join('');

        if (filteredData !== pasteData) {
            event.preventDefault();

            // Insert the filtered text
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            const currentValue = target.value;
            const newValue = currentValue.substring(0, start) + filteredData + currentValue.substring(end);

            if (onChange) {
                const syntheticEvent = {
                    ...event,
                    target: {
                        ...target,
                        value: newValue
                    }
                };
                onChange(syntheticEvent);
            }

            showInvalidCharFeedback();
        }
    };

    const handleBlur = (event) => {
        const originalValue = event.target.value;
        const cleanedValue = cleanText(originalValue);

        // If value needs cleaning, update it and trigger onChange
        if (cleanedValue !== originalValue && onChange) {
            const syntheticEvent = {
                ...event,
                target: {
                    ...event.target,
                    value: cleanedValue
                }
            };
            onChange(syntheticEvent);
        }

        // Call original onBlur handler if it exists
        if (onBlur) {
            onBlur(event);
        }
    };

    return (
        <MuiTextField
            {...props}
            onChange={onChange}      // Normal onChange - no interference
            onBlur={handleBlur}      // Clean up spacing on blur
            onKeyPress={handleKeyPress}  // Prevent invalid characters
            onPaste={handlePaste}    // Handle paste events
            error={showError || props.error}  // Show error state for invalid chars
            helperText={feedbackMessage || props.helperText}  // Show feedback message
            FormHelperTextProps={{
                ...props.FormHelperTextProps,
                style: {
                    color: showError ? '#d32f2f' : undefined,
                    ...props.FormHelperTextProps?.style
                }
            }}
        />
    );
};

export default TrimmedTextField;